import { createQueryKeys } from '@lukemorales/query-key-factory';
import { AccountAdminApiV2 } from '../../../../lib/api/account/admin/AccountAdminApiV2';
import { CouponApiV2 } from '../../../../lib/api/coupon/CouponApiV2';
import { CouponDtoDiscountTypeEnum, CouponDtoStatusEnum } from '../../../../types/api/coupons';
import EventApiV2 from '../../../../lib/api/event/EventApiV2';
import { EventsFindResponseBodyEventsDtoStatusEnum } from '../../../../types/api/events/events-find-response-body-events-dto';
import { COUPON_DISCOUNT_TYPE } from '../constants';
import {
  PromotionScheduleCoupon,
  PromotionScheduleEvent,
  PromotionScheduleLog,
  PromotionSchedule,
  PromotionSchedulesFindQuery,
  PromotionSchedulesFindResponse,
  PromotionSchedulesListQuery,
  PromotionSchedulesListResponse,
  promotionScheduleApi,
} from '../../../../lib/api/promotionSchedule';

/** 담당자 선택 옵션. 관리자 수가 많지 않아 한 번에 받아 클라이언트에서 검색한다. */
export type ManagerOption = {
  id: number;
  name: string;
  email: string;
  department: string | null;
};

const MANAGER_PAGE_SIZE = 500;

/**
 * 활성 관리자 목록을 담당자 선택 옵션으로 변환.
 *
 * `AccountAdminApiV2.getAccountAdminList` 의 선언 타입(`Array<AccountAdminInfoResponse[]>`)은
 * 실제 응답(`{ data: Admin[], total }`)과 맞지 않아 여기서 최소 shape 으로 다시 좁힌다.
 * (기존 타입은 다른 화면이 쓰고 있어 이 작업에서 건드리지 않는다 — 정정은 AMZUWEB-670.)
 */
const fetchManagerOptions = async (): Promise<ManagerOption[]> => {
  const response = (await AccountAdminApiV2.getAccountAdminList({
    page: 1,
    pageSize: MANAGER_PAGE_SIZE,
    status: 'active',
  } as never)) as unknown as {
    data: { id: number; name: string; email: string; department?: string | null }[];
  };

  return (response?.data ?? []).map((admin) => ({
    id: admin.id,
    name: admin.name,
    email: admin.email,
    department: admin.department ?? null,
  }));
};

/** 연결할 기획전 검색 결과 상한 — 이름으로 좁혀 쓰는 것을 전제한다 */
const EVENT_SEARCH_SIZE = 50;

/** 목록 API 의 문자열 상태 → 스케줄 API 의 DB 원값 (0: 비활성, 1: 활성, 2: 무기한) */
const EVENT_STATUS_BY_TEXT: Record<string, number> = {
  [EventsFindResponseBodyEventsDtoStatusEnum.Inactive]: 0,
  [EventsFindResponseBodyEventsDtoStatusEnum.Active]: 1,
  [EventsFindResponseBodyEventsDtoStatusEnum.Unlimited]: 2,
};

/**
 * 기획전 검색 — 스케줄에 연결할 기획전을 고르기 위한 조회.
 *
 * ⚠️ **두 API 의 표현이 다르다** (쿠폰과 같은 문제). 목록 API 는 상태를 문자열 enum
 * (`'active'`)으로 주는데 스케줄 API 는 DB 원값(숫자)을 그대로 준다. 화면이 한 가지 모양만
 * 다루도록 **여기서 숫자로 맞춘다** — 이 변환이 없으면 검색 결과와 연결된 기획전이 같은
 * 화면에서 서로 다른 규약으로 그려진다.
 *
 * 기간이 null 인 기획전도 있어(무기한 노출) 빈 문자열로 받아 화면이 판단하게 둔다.
 */
const fetchEventOptions = async (keyword: string): Promise<PromotionScheduleEvent[]> => {
  const response = await EventApiV2.getEventList(
    {
      page: 1,
      pageSize: EVENT_SEARCH_SIZE,
      ...(keyword ? { mainTitle: keyword } : {}),
    },
    {
      /**
       * 타이핑을 따라 나가는 조회라 전역 로더를 끈다 — 켜두면 글자마다 어드민 전체가
       * 어두워진다. 로딩 표시는 Select 의 `notFoundContent` 가 맡는다 (AMZUWEB-673 선례).
       */
      skipFullscreenLoader: true,
    },
  );

  return (response?.events ?? []).map((event) => ({
    id: event.id,
    mainTitle: event.mainTitle,
    startDate: event.startDate ?? '',
    endDate: event.endDate ?? '',
    status: EVENT_STATUS_BY_TEXT[event.status] ?? 0,
  }));
};

/** 연결할 쿠폰 검색 결과 상한 — 전체가 5천 건대라 이름으로 좁혀 쓰는 것을 전제한다 */
const COUPON_SEARCH_SIZE = 50;

/**
 * 쿠폰 검색 — 스케줄에 연결할 쿠폰을 고르기 위한 조회.
 *
 * 활성 쿠폰만 본다: 비활성 쿠폰을 계획에 걸어두면 화면에는 붙어 있는데 실제로는 발급되지 않는다.
 *
 * ⚠️ **쿠폰 목록 API 와 스케줄 API 의 표현이 다르다.**
 * 목록 API 는 문자열 enum(`'rate'`/`'amount'`, `'active'`)으로 내려주고 기간을 `date` 에 중첩하는데,
 * 스케줄 API 는 DB 원값(숫자)을 그대로 준다. 화면이 한 가지 모양만 다루도록 **여기서 숫자로 맞춘다** —
 * 이 변환이 없으면 검색 결과와 연결된 쿠폰이 같은 화면에서 서로 다른 규약으로 그려진다.
 */
const fetchCouponOptions = async (keyword: string): Promise<PromotionScheduleCoupon[]> => {
  const response = await CouponApiV2.getCouponList(
    {
      page: 1,
      pageSize: COUPON_SEARCH_SIZE,
      status: CouponDtoStatusEnum.Active,
      ...(keyword ? { name: keyword } : {}),
    },
    {
      /**
       * ⚠️ 타이핑을 따라 나가는 조회라 전역 로더를 끈다.
       *
       * 켜두면 `썸`→`썸머`→`썸머세` 마다 어드민 전체가 어두워진다 — 디바운스로 막으려던
       * 목록 깜빡임보다 큰 깜빡임이 된다. 어드민의 기존 서버 검색은 전부 버튼식이라
       * 타이핑 연동은 이 화면이 처음이고, 로딩 표시는 Select 의 `notFoundContent` 가 맡는다.
       */
      skipFullscreenLoader: true,
    },
  );

  return (response?.coupons ?? []).map((coupon) => ({
    id: coupon.id,
    name: coupon.name,
    discountType:
      coupon.discountType === CouponDtoDiscountTypeEnum.Rate ? COUPON_DISCOUNT_TYPE.RATE : COUPON_DISCOUNT_TYPE.AMOUNT,
    discountAmount: coupon.discountAmount,
    // 날짜 지정 쿠폰만 기간이 있다 — 발급일 기준·무기한은 null
    startDate: coupon.date?.startDate ?? null,
    endDate: coupon.date?.endDate ?? null,
  }));
};

export const promotionScheduleQuery = createQueryKeys('promotionSchedule', {
  timeline: (params: PromotionSchedulesFindQuery) => ({
    queryKey: [params],
    queryFn: (): Promise<PromotionSchedulesFindResponse> => promotionScheduleApi.findAll(params),
  }),
  list: (params: PromotionSchedulesListQuery) => ({
    queryKey: [params],
    queryFn: (): Promise<PromotionSchedulesListResponse> => promotionScheduleApi.findList(params),
  }),
  detail: (promotionScheduleId: number) => ({
    queryKey: [promotionScheduleId],
    queryFn: (): Promise<PromotionSchedule> => promotionScheduleApi.findOne(promotionScheduleId),
  }),
  logs: (promotionScheduleId: number) => ({
    queryKey: [promotionScheduleId],
    queryFn: (): Promise<PromotionScheduleLog[]> => promotionScheduleApi.getLogs(promotionScheduleId),
  }),
  managers: {
    queryKey: null,
    queryFn: fetchManagerOptions,
  },
  coupons: (keyword: string) => ({
    queryKey: [keyword],
    queryFn: (): Promise<PromotionScheduleCoupon[]> => fetchCouponOptions(keyword),
  }),
  events: (keyword: string) => ({
    queryKey: [keyword],
    queryFn: (): Promise<PromotionScheduleEvent[]> => fetchEventOptions(keyword),
  }),
});
